/**
 * Auto-fill grid of category cards (16:10, image or accent gradient,
 * bottom-overlay name + count). Ported from ThemeMaker's
 * {@code blocks/grid/CategoryGrid}.
 *
 * <p>Author supplies {@code items} directly — no orchestrator dataSpec
 * coupling. Hosts wanting dynamic category feeds resolve them upstream
 * and pass the resolved list in.
 *
 * <p>{@code columns} (3–8) maps to a min card width so the grid feels
 * "browsable" rather than "empty"; the actual column count adapts to
 * viewport width via {@code auto-fill}.
 */
'use client';

import { useBlockPart } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList, asNumber } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import { EmptyState } from '../_shared/EmptyState';

type Localized = string | Record<string, string>;

export interface CategoryGridItem {
  id?: string | number;
  name?: Localized;
  slug?: string;
  count?: number;
  image?: string;
  color?: string;
  href?: string;
}

export interface CategoryGridProps {
  title?: Localized;
  columns?: number;
  items?: CategoryGridItem[];
  /** Optional href builder: receives the item, returns absolute path.
   *  Falls back to {@code item.href}, then to {@code `/c/${slug}`}. */
  itemHref?: (item: CategoryGridItem) => string;
  emptyTitle?: Localized;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function CategoryGrid({
  title,
  columns,
  items,
  itemHref,
  emptyTitle = 'No categories available',
}: CategoryGridProps) {
  const part = useBlockPart();
  const cols = clamp(asNumber(columns, 5), 3, 8);
  const list = asList<CategoryGridItem>(items);
  const label = tx(title) || 'Browse categories';
  const minCardWidth = clamp(Math.round(1100 / Math.max(1, cols)), 160, 280);

  if (list.length === 0) {
    return (
      <SectionShell title={label}>
        <EmptyState title={tx(emptyTitle)} />
      </SectionShell>
    );
  }

  const hrefFor = (c: CategoryGridItem): string => {
    if (itemHref) return itemHref(c);
    if (c.href) return c.href;
    return c.slug ? `/c/${c.slug}` : '#';
  };

  return (
    <SectionShell title={label}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .cat-grid-card {
          min-height: 260px;
          transition: transform 240ms ease, box-shadow 240ms ease;
        }
        .cat-grid-card:hover {
          transform: translateY(-5px);
          box-shadow: var(--shadow-xl, 0 24px 48px rgba(0,0,0,.16));
        }
        .cat-grid-card__arrow {
          display: grid;
          width: 36px;
          height: 36px;
          flex: none;
          place-items: center;
          border: 1px solid rgba(255,255,255,.5);
          border-radius: 999px;
          background: rgba(255,255,255,.16);
          backdrop-filter: blur(10px);
          transition: transform 180ms ease, background 180ms ease;
        }
        .cat-grid-card:hover .cat-grid-card__arrow {
          transform: translateX(3px);
          background: rgba(255,255,255,.28);
        }
        @media (prefers-reduced-motion: reduce) {
          .cat-grid-card, .cat-grid-card__arrow { transition: none; }
          .cat-grid-card:hover, .cat-grid-card:hover .cat-grid-card__arrow { transform: none; }
        }
      `,
        }}
      />
      <div
        {...part('list')}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(min(${minCardWidth}px, 100%), 1fr))`,
          gap: 'clamp(10px, 1.6vw, 14px)',
        }}
      >
        {list.map((c, i) => {
          const href = hrefFor(c);
          const accent = c.color || 'var(--color-brand-500, #6366f1)';
          return (
            <Card asChild key={c.id ?? i} className="ring-0">
              <a
                {...part('item', i)}
                href={href}
                className="cat-grid-card"
                style={{
                  position: 'relative',
                  display: 'block',
                  borderRadius: 'var(--radius-card, 12px)',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  color: 'var(--color-text-on-brand, #fff)',
                  aspectRatio: '4 / 5',
                  background: c.image
                    ? `url("${c.image}") center/cover, ${accent}`
                    : `linear-gradient(135deg, ${accent}, ${accent}dd)`,
                }}
              >
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(to bottom, rgba(0,0,0,0.02) 20%, rgba(0,0,0,0.78) 100%)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: 'clamp(16px, 2vw, 24px)',
                    color: 'var(--color-text-on-brand, #fff)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                      <div
                        style={{
                          fontWeight: 650,
                          fontSize: 'clamp(18px, 1.8vw, 24px)',
                          lineHeight: 1.1,
                          textShadow: '0 1px 8px rgba(0,0,0,0.45)',
                        }}
                      >
                        {tx(c.name)}
                      </div>
                      {typeof c.count === 'number' && c.count > 0 && (
                        <div
                          style={{
                            marginTop: 7,
                            fontSize: '12px',
                            fontWeight: 600,
                            opacity: 0.9,
                            textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                          }}
                        >
                          {c.count} {c.count === 1 ? 'item' : 'items'}
                        </div>
                      )}
                    </div>
                    <span className="cat-grid-card__arrow" aria-hidden="true">→</span>
                  </div>
                </div>
              </a>
            </Card>
          );
        })}
      </div>
    </SectionShell>
  );
}
